import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, LogOut, Building2, Check, Menu, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function todayLabel(tz) {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
    timeZone: tz || undefined,
  });
}

function firstName(user, activeClinic) {
  const meta = user?.user_metadata?.full_name;
  if (meta) return meta.split(" ")[0];
  if (activeClinic?.clinic_name) return activeClinic.clinic_name.split(" ")[0];
  if (user?.email) return user.email.split("@")[0];
  return "there";
}

export default function Header({ onMenuClick }) {
  const { user, clinics, activeClinic, switchClinic, signOut } = useAuth();
  const { branches, activeBranch, activeBranchId, switchBranch } = useBranch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const compact = location.pathname === "/ortho-center";
  const switcherLabel = activeBranch?.name || activeClinic?.clinic_name || "No clinic";

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const tz = activeClinic?.timezone;

  return (
    <header
      data-testid="app-header"
      className={[
        "bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-10 flex items-center gap-3 sm:gap-4",
        compact ? "py-2" : "py-4 sm:items-start sm:py-6 lg:py-7",
      ].join(" ")}
    >
      {/* Mobile / tablet hamburger */}
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Open menu"
        data-testid="header-menu-button"
        className="lg:hidden -ml-1 p-2 rounded-md text-slate-600 hover:bg-slate-100 active:bg-slate-200"
      >
        <Menu className="w-6 h-6" strokeWidth={2} />
      </button>

      <div className="flex-1 min-w-0">
        {compact ? null : (
          <>
            <h1
              data-testid="header-greeting"
              className="text-lg sm:text-2xl lg:text-[28px] leading-tight font-bold text-slate-900 truncate"
            >
              {greeting()}, {firstName(user, activeClinic)} <span aria-hidden>👋</span>
            </h1>
            <p
              data-testid="header-date"
              className="mt-0.5 sm:mt-1 text-xs sm:text-[14px] text-slate-500 truncate"
            >
              {todayLabel(tz)}
            </p>
          </>
        )}
      </div>

      <div ref={ref} className="relative shrink-0">
        <button
          data-testid="clinic-switcher-button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-teal-300 transition"
        >
          <Building2 className="w-4 h-4 text-teal-600" strokeWidth={2} />
          <span className="hidden sm:inline text-sm font-medium text-slate-900 max-w-[140px] sm:max-w-[180px] truncate">
            {switcherLabel}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={2} />
        </button>

        {open ? (
          <div
            data-testid="clinic-switcher-menu"
            className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-72 max-w-[20rem] rounded-xl bg-white border border-slate-200 shadow-lg p-1 z-30"
          >
            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold tracking-wider uppercase text-slate-400">
              Organization
            </div>
            {clinics.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">No clinics yet.</div>
            ) : null}
            {clinics.map((c) => {
              const isActive = c.is_active_clinic;
              return (
                <button
                  key={c.clinic_id}
                  data-testid={`clinic-switcher-item-${c.clinic_id}`}
                  onClick={async () => {
                    setOpen(false);
                    if (!isActive) await switchClinic(c.clinic_id);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-start gap-2 hover:bg-slate-50 ${isActive ? "bg-teal-50" : ""}`}
                >
                  <div className="mt-0.5">
                    {isActive ? <Check className="w-4 h-4 text-teal-600" strokeWidth={2.5} /> : <div className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.clinic_name}</div>
                    <div className="text-xs text-slate-500 capitalize truncate">
                      {c.role.replace(/_/g, " ")} · {c.currency} · {c.timezone}
                    </div>
                  </div>
                </button>
              );
            })}
            <div className="my-1 h-px bg-slate-100" />
            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold tracking-wider uppercase text-slate-400">
              Branch
            </div>
            {branches.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">No active branches.</div>
            ) : null}
            {branches.map((branch) => {
              const selected = branch.id === activeBranchId;
              return (
                <button
                  key={branch.id}
                  data-testid={`branch-switcher-item-${branch.id}`}
                  onClick={() => {
                    setOpen(false);
                    switchBranch(branch.id);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-start gap-2 hover:bg-slate-50 ${selected ? "bg-teal-50" : ""}`}
                >
                  <div className="mt-0.5">
                    {selected ? <Check className="w-4 h-4 text-teal-600" strokeWidth={2.5} /> : <MapPin className="w-4 h-4 text-slate-300" strokeWidth={2} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{branch.name}</div>
                    <div className="text-xs text-slate-500 truncate">{branch.code || "No code"}</div>
                  </div>
                </button>
              );
            })}
            <div className="my-1 h-px bg-slate-100" />
            <div className="px-3 py-1.5 text-xs text-slate-500 truncate">
              {user?.email}
            </div>
            <button
              data-testid="header-signout-button"
              onClick={async () => { setOpen(false); await signOut(); }}
              className="w-full text-left px-3 py-2 rounded-md flex items-center gap-2 hover:bg-rose-50 text-sm text-rose-600"
            >
              <LogOut className="w-4 h-4" strokeWidth={2} />
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
